import React, { useRef, useState, useEffect } from "react";
import style from "./project-card.module.css";
import { useHover, useOnClickOutside } from "usehooks-ts";
import RestrictedUserModal from "frontend/modals/restricted-user-new-modal";
import { MenuHorizontalIcon } from "frontend/icons/menu-horizontal";
import { Project } from "shared/datamodel/schemas/project";
import InstanceMenu from "./side-menu/instance-menu";
import { Team } from "shared/datamodel/schemas/team";
import { CanvasTemplate } from "shared/datamodel/schemas";
import { useProjectsCache } from "frontend/hooks/caching/use-projects-cache";
import InviteModalNew from "frontend/modals/invite-modal-new";
import Modal from "frontend/modal/modal";
import { CollaborationType, InviteMode } from "state-atoms";
import { InstanceType } from "shared/datamodel/schemas/ability";
import ManageCollaborationMembers from "frontend/modals/collaboration/manage-collaboration-members";
import LeaveCollaborationModalNew from "frontend/modals/collaboration/leave-collaboration-modal-new";
import DeleteCollaborationModalNew from "frontend/modals/collaboration/delete-collaboration-modal-new";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import { ProjectBigIcon } from "frontend/icons/project-big";
import RenameModal from "frontend/modals/collaboration/rename-modal";

const projectIconColors = ["#D6BAFF", "#00B875", "#FF7A53", "#FFB200", "#FFB4C9", "#F26743", "#0091E5"];

export function ProjectCard({
  setSelectedProject,
  project,
  parentTeam,
  createNewBoard,
  onEditSeats,
}: {
  setSelectedProject: (projectId: string) => void;
  project: Project;
  parentTeam: Team;
  createNewBoard: ({
    template,
    inputProjectId,
    inputTeamId,
    source,
  }: {
    template: CanvasTemplate;
    inputProjectId: string | null;
    inputTeamId: string | null;
    source: string;
  }) => void;
  onEditSeats: () => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const [showRestrictedModal, setShowRestrictedModal] = useState<boolean>(false);
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [showMembersModal, setShowMembersModal] = useState<boolean>(false);
  const [showLeaveModal, setShowLeaveModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [projectName, setProjectName] = useState<string>(project.name);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const menuButtonRef = useRef(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLDivElement | null>(null);
  const hover = useHover(ref);

  const { updateProject: handleEditProjectName } = useProjectsCache();

  useEffect(() => {
    setProjectName(project.name);
  }, [project.name]);

  function menuClicked(e: React.MouseEvent) {
    e.stopPropagation();
    setShowMenu(!showMenu);
  }

  useOnClickOutside(menuRef, () => {
    setShowMenu(false);
  });

  const renderMenu = () => {
    return (
      <InstanceMenu
        menuButtonRef={menuButtonRef}
        menuRef={menuRef}
        setShowMenu={setShowMenu}
        project={project as Project}
        team={parentTeam}
        setShowInviteModal={setShowInviteModal}
        setShowMembersModal={setShowMembersModal}
        createNewBoard={createNewBoard}
        renameClicked={() => setShowRenameModal(true)}
        setShowLeaveModal={setShowLeaveModal}
        setShowDeleteModal={setShowDeleteModal}
      />
    );
  };

  const editProjectName = (newName: string) => {
    setProjectName(newName);
    handleEditProjectName({ ...project, name: newName });
  };

  function renderMembersModal() {
    if (showMembersModal && project) {
      return (
        <Modal dimBackground={true}>
          <ManageCollaborationMembers
            onDismiss={() => {
              setShowMembersModal(false);
            }}
            instanceId={project.documentId}
            type={InstanceType.Project}
            onEditSeats={onEditSeats}
          />
        </Modal>
      );
    }
    return null;
  }

  const renderLeaveModal = () => {
    if (showLeaveModal && project) {
      return (
        <Modal dimBackground={true}>
          <LeaveCollaborationModalNew
            type={CollaborationType.Project}
            onDismiss={() => {
              setShowLeaveModal(false);
            }}
            subtitle={`You will lose access to "${projectName}" project and it's canvases.`}
            project={project}
          />
        </Modal>
      );
    } else {
      return null;
    }
  };

  const renderDeleteModal = () => {
    if (showDeleteModal && project) {
      return (
        <Modal dimBackground={true}>
          <DeleteCollaborationModalNew
            type={CollaborationType.Project}
            onDismiss={() => {
              setShowDeleteModal(false);
            }}
            subtitle={`All projects and canvases within this project will be permanently deleted. You can move canvases to another project or export them before deleting.`}
            project={project}
            isSelected={true}
          />
        </Modal>
      );
    } else {
      return null;
    }
  };

  const renderRenameModal = () => {
    if (showRenameModal) {
      return (
        <Modal dimBackground={true}>
          <RenameModal
            currentName={projectName}
            onDismiss={() => setShowRenameModal(false)}
            type={InstanceType.Project}
            onEditName={editProjectName}
          />
        </Modal>
      );
    }
    return null;
  };

  return (
    <>
      <div
        className={style.wrapper}
        ref={ref}
        onClick={() => setSelectedProject(project.documentId)}
        data-testid={projectName}
      >
        <div className={style.container}>
          {hover && projectName.length > 15 && (
            <Tooltip
              side="right"
              label={projectName}
              relativeTo={ref}
              customStyle={{
                backgroundColor: "#DEE1EB",
                color: " #113357",
                fontSize: 12,
                fontWeight: 400,
                borderRadius: 4,
                maxWidth: "500px",
                wordBreak: "break-word",
              }}
              withArrow={true}
            />
          )}
          {(hover || showMenu) && (
            <div className={style.menuIcon} onClick={menuClicked} ref={menuButtonRef} data-testid="project-card-menu">
              <MenuHorizontalIcon />
            </div>
          )}
          <div className={style.projectNameContainer}>
            <div className={style.projectIcon}>
              <ProjectBigIcon color={projectIconColors[Number(project.urlId) % projectIconColors.length]} />
            </div>
            <div className={style.projectName}>{projectName}</div>
          </div>
        </div>
        {showRestrictedModal && (
          <RestrictedUserModal trackingEvent={"home"} onDismiss={() => setShowRestrictedModal(false)} />
        )}
      </div>
      {showMenu && renderMenu()}
      {showInviteModal && project && (
        <Modal dimBackground={true}>
          <InviteModalNew
            instance={project}
            onDismiss={() => setShowInviteModal(false)}
            inviteMode={InviteMode.Edit}
            onEditSeats={onEditSeats}
            source={`project-invite-modal`}
            type={InstanceType.Project}
            parentInstance={parentTeam as Team}
          />
        </Modal>
      )}
      {renderMembersModal()}
      {renderLeaveModal()}
      {renderDeleteModal()}
      {renderRenameModal()}
    </>
  );
}
